import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useChat } from '../contexts/ChatContext';
import {
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  LightBulbIcon,
  AcademicCapIcon,
  BookOpenIcon,
  QuestionMarkCircleIcon,
  ClockIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  DocumentTextIcon,
  MapPinIcon,
  InformationCircleIcon,
  SparklesIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { renderTextWithLinks } from '../utils/formatUtils';
import { db } from '../firebase/config';
import { collection, addDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { AIService } from '../utils/aiService';
import CustomDropdown from './CustomDropdown';

interface AIChatbotProps {
  isOpen: boolean;
  onClose: () => void;
  chatHistory: any[];
  onSendMessage: (message: string, type?: string, category?: string) => Promise<any>;
  isTyping: boolean;
  error: string | null;
  onClearError: () => void;
  fullScreen?: boolean;
}

const AIChatbot: React.FC<AIChatbotProps> = ({
  isOpen,
  onClose,
  chatHistory,
  onSendMessage,
  isTyping,
  error,
  onClearError,
  fullScreen = false
}) => {
  const { currentUser } = useAuth();
  const [message, setMessage] = useState('');
  const [selectedType, setSelectedType] = useState<'question' | 'support' | 'learning'>('question');
  const [selectedCategory, setSelectedCategory] = useState<'course_content' | 'assignment' | 'exam_prep' | 'technical' | 'general'>('general');
  const [activeTab, setActiveTab] = useState(0); // New state for tabbed interface
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    courseName: '',
    program: '',
    currentLevel: '',
    targetExam: '',
    studyTime: '',
    specificTopics: '',
    textbookContent: ''
  });
  const [selectedAction, setSelectedAction] = useState<any>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({
    title: '',
    message: '',
    type: 'info' as 'info' | 'success' | 'error',
    onConfirm: () => { }
  });
  const [aiServiceStatus, setAiServiceStatus] = useState<'checking' | 'available' | 'unavailable'>('checking');
  const [showAIServiceInfo, setShowAIServiceInfo] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initialize AI Service
  const aiService = AIService.getInstance();

  // AI Service status is managed by parent components
  useEffect(() => {
    if (isOpen) {
      setAiServiceStatus('available'); // Default to available, parent will sync if needed
    }
  }, [isOpen]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const showCustomAlert = useCallback((title: string, message: string, type: 'info' | 'success' | 'error' = 'info') => {
    setConfirmModalData({
      title,
      message,
      type,
      onConfirm: () => setShowConfirmModal(false)
    });
    setShowConfirmModal(true);
  }, []);

  const showCustomConfirm = useCallback((title: string, message: string, onConfirm: () => void) => {
    setConfirmModalData({
      title,
      message,
      type: 'info',
      onConfirm: () => {
        onConfirm();
        setShowConfirmModal(false);
      }
    });
    setShowConfirmModal(true);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, scrollToBottom]);

  const handleSaveToPractice = async (response: string, question: string) => {
    try {
      if (!currentUser?.uid) {
        showCustomAlert('Login Required', 'Please log in to save practice items.', 'error');
        return;
      }

      const practiceItemsCollectionRef = collection(db, 'practiceItems');
      await addDoc(practiceItemsCollectionRef, {
        userId: currentUser.uid,
        question: question,
        answer: response,
        timestamp: new Date().toISOString(),
        source: 'AI Chat'
      });
      showCustomAlert('Success!', 'Saved to Practice! You can view it in the Practice page.', 'success');
    } catch (error) {
      console.error('Error saving to practice:', error);
      showCustomAlert('Error', 'Failed to save to practice. Please try again.', 'error');
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    await onSendMessage(message, selectedType, selectedCategory);
    setMessage('');
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast here if react-hot-toast is used
    console.log('Copied to clipboard');
  };

  const handleActionClick = (action: any) => {
    if (['Study Plan', 'Schedule', 'Review', 'Explain', 'Clarify', 'Examples', 'Simplify', 'Practice', 'Quiz', 'Summary', 'Tips'].includes(action.text)) {
      setSelectedAction(action);
      setShowForm(true);
    } else {
      setMessage(action.prompt);
      handleSendMessage();
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAction) return;

    const enhancedPrompt = `${selectedAction.prompt}

Course Details:
- Course Name: ${formData.courseName}
- Program: ${formData.program}
- Current Level: ${formData.currentLevel}
- Target Exam: ${formData.targetExam}
- Available Study Time: ${formData.studyTime}
- Specific Topics to Focus: ${formData.specificTopics}
${formData.textbookContent ? `- Textbook Content: ${formData.textbookContent}` : ''}`;

    setMessage(enhancedPrompt);
    setShowForm(false);
    setFormData({
      courseName: '',
      program: '',
      currentLevel: '',
      targetExam: '',
      studyTime: '',
      specificTopics: '',
      textbookContent: ''
    });
    setSelectedAction(null);

    await handleSendMessage();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'question':
        return '❓';
      case 'support111':
        return '🛠️';
      case 'learning':
        return '📚';
      default:
        return '💬';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'course_content':
        return 'bg-indigo-100 text-indigo-800';
      case 'assignment':
        return 'bg-emerald-100 text-emerald-800';
      case 'exam_prep':
        return 'bg-violet-100 text-violet-800';
      case 'technical':
        return 'bg-rose-100 text-rose-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {fullScreen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998]" onClick={onClose} />
      )}
      <div className={`${fullScreen
        ? 'fixed inset-0 z-[9999] w-full h-full bg-slate-50'
        : 'fixed bottom-4 right-4 z-[9999] w-96 h-[560px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300 ease-in-out'
        } flex flex-col`}>
        {/* Header - Standard Professional Look */}
        <div className={`flex items-center justify-between p-4 ${fullScreen ? 'bg-indigo-600' : 'bg-white border-b border-slate-100'} shadow-sm relative z-10`}>
          <div className="flex items-center min-w-0">
            <div className={`w-8 h-8 ${fullScreen ? 'bg-white/20' : 'bg-indigo-600'} rounded-lg flex items-center justify-center mr-3 flex-shrink-0 transition-transform hover:scale-105`}>
              <ChatBubbleLeftRightIcon className={`w-5 h-5 ${fullScreen ? 'text-white' : 'text-white'}`} />
            </div>
            <div className="min-w-0">
              <h3 className={`font-bold text-base truncate tracking-tight ${fullScreen ? 'text-white' : 'text-slate-900'}`}>
                {fullScreen ? 'Profstudymate' : 'Profstudymate'}
              </h3>
              <div className="flex items-center space-x-2">
                <span className={`w-2 h-2 rounded-full ${aiServiceStatus === 'available' ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}></span>
                <p className={`text-[11px] font-medium truncate ${fullScreen ? 'text-indigo-100' : 'text-slate-500'}`}>
                  Profstudymate AI • {aiServiceStatus === 'available' ? 'Online' : 'Connecting...'}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={onClose}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${fullScreen ? 'bg-white/20 hover:bg-white/30 text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'}`}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* AI Service Status Info */}
        {showAIServiceInfo && (
          <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-indigo-50 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {aiServiceStatus === 'checking' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                    <span className="text-sm font-medium text-indigo-700">Connecting to Profstudymate...</span>
                  </>
                ) : aiServiceStatus === 'available' ? (
                  <>
                    <SparklesIcon className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-medium text-slate-700">Profstudymate AI is active</span>
                  </>
                ) : (
                  <>
                    <CogIcon className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm text-yellow-600">AI service limited</span>
                  </>
                )}
              </div>
              <button
                onClick={() => setShowAIServiceInfo(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            {aiServiceStatus === 'unavailable' && (
              <p className="text-xs text-yellow-600 mt-1">
                Some features may not work. Check your API configuration.
              </p>
            )}
          </div>
        )}


        {/* Messages */}
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${fullScreen ? 'max-h-[calc(100vh-200px)]' : ''}`}>
          {chatHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6 animate-fadeIn">
              <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-indigo-100">
                <SparklesIcon className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">How can I help you learn today?</h3>
              <p className="text-sm text-slate-500 max-w-[280px]">Ask me any question about your courses, exams, or for a personalized study plan.</p>
            </div>
          ) : (
            chatHistory.map((msg, index) => (
              <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`group relative max-w-[85%] px-4 py-3 rounded-2xl ${msg.role === 'user'
                  ? 'bg-indigo-600 text-white rounded-tr-sm shadow-indigo-100'
                  : 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm shadow-sm'
                  } transition-all duration-200 hover:shadow-md`}>
                  <p className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: renderTextWithLinks(msg.content) }} />

                  <div className={`flex items-center mt-2.5 pt-2 border-t space-x-2 ${msg.role === 'user' ? 'border-white/10 justify-end' : 'border-slate-50 justify-between'}`}>
                    <span className={`text-[10px] font-medium opacity-60`}>
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>

                    {msg.role === 'assistant' && (
                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <button
                          onClick={() => handleCopy(msg.content)}
                          className="p-1 text-slate-400 hover:text-indigo-600 transition-colors"
                          title="Copy"
                        >
                          <DocumentTextIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleSaveToPractice(msg.content, 'AI Response')}
                          className="p-1 text-slate-400 hover:text-indigo-600 transition-colors"
                          title="Save to Practice"
                        >
                          <AcademicCapIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex justify-start pl-2">
              <div className="bg-white border border-slate-200 p-4 rounded-2xl rounded-tl-sm shadow-sm">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce"></div>
                    <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                    <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                  </div>
                  <span className="text-xs font-medium text-slate-500 ml-1">Profstudymate is thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Course Details Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedAction?.text} - Course Details
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedAction?.text === 'Study Plan' && 'Create a personalized study plan'}
                      {selectedAction?.text === 'Schedule' && 'Plan your study schedule'}
                      {selectedAction?.text === 'Review' && 'Review your learning progress'}
                      {selectedAction?.text === 'Explain' && 'Get detailed explanations'}
                      {selectedAction?.text === 'Clarify' && 'Clear up confusion'}
                      {selectedAction?.text === 'Examples' && 'Get real-world examples'}
                      {selectedAction?.text === 'Simplify' && 'Break down complex topics'}
                      {selectedAction?.text === 'Practice' && 'Generate practice questions'}
                      {selectedAction?.text === 'Quiz' && 'Create a custom quiz'}
                      {selectedAction?.text === 'Summary' && 'Get key points summary'}
                      {selectedAction?.text === 'Tips' && 'Receive study tips and advice'}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowForm(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Course Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.courseName}
                      onChange={(e) => setFormData({ ...formData, courseName: e.target.value })}
                      placeholder="e.g., Advanced Taxation, Financial Accounting"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Program *
                    </label>
                    <CustomDropdown
                      value={formData.program}
                      onChange={(value) => setFormData({ ...formData, program: value })}
                      options={[
                        { value: "", label: "Select Program" },
                        { value: "ICAG", label: "ICAG (Institute of Chartered Accountants Ghana)" },
                        { value: "CITG", label: "CITG (Chartered Institute of Taxation Ghana)" },
                        { value: "Corporate Training", label: "Corporate Training" },
                        { value: "Digital Marketing", label: "Digital Marketing" },
                        { value: "Other", label: "Other" }
                      ]}
                      placeholder="Select Program"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Level
                    </label>
                    <input
                      type="text"
                      value={formData.currentLevel}
                      onChange={(e) => setFormData({ ...formData, currentLevel: e.target.value })}
                      placeholder="e.g., Beginner, Intermediate, Advanced"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Exam Date
                    </label>
                    <input
                      type="text"
                      value={formData.targetExam}
                      onChange={(e) => setFormData({ ...formData, targetExam: e.target.value })}
                      placeholder="e.g., December 2024, March 2025"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Available Study Time
                    </label>
                    <input
                      type="text"
                      value={formData.studyTime}
                      onChange={(e) => setFormData({ ...formData, studyTime: e.target.value })}
                      placeholder="e.g., 2 hours daily, Weekends only"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Specific Topics to Focus
                    </label>
                    <textarea
                      value={formData.specificTopics}
                      onChange={(e) => setFormData({ ...formData, specificTopics: e.target.value })}
                      placeholder={
                        selectedAction?.text === 'Study Plan' ? 'e.g., Tax calculations, Financial statements, Audit procedures'
                          : selectedAction?.text === 'Schedule' ? 'e.g., Core topics, Weak areas, Exam preparation'
                            : selectedAction?.text === 'Review' ? 'e.g., Completed modules, Areas of confusion, Recent topics'
                              : selectedAction?.text === 'Explain' ? 'e.g., Complex concepts, Difficult topics, Unclear areas'
                                : selectedAction?.text === 'Clarify' ? 'e.g., Confusing topics, Unclear concepts, Mixed-up ideas'
                                  : selectedAction?.text === 'Examples' ? 'e.g., Tax scenarios, Financial cases, Real situations'
                                    : selectedAction?.text === 'Simplify' ? 'e.g., Complex theories, Advanced concepts, Difficult topics'
                                      : selectedAction?.text === 'Practice' ? 'e.g., Problem areas, Test topics, Practice subjects'
                                        : selectedAction?.text === 'Quiz' ? 'e.g., Quiz topics, Test areas, Assessment subjects'
                                          : selectedAction?.text === 'Summary' ? 'e.g., Key concepts, Main points, Important topics'
                                            : selectedAction?.text === 'Tips' ? 'e.g., Study areas, Learning topics, Focus subjects'
                                              : 'e.g., Tax calculations, Financial statements, Audit procedures'
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Textbook Content (Optional)
                    </label>
                    <textarea
                      value={formData.textbookContent}
                      onChange={(e) => setFormData({ ...formData, textbookContent: e.target.value })}
                      placeholder="Provide specific textbook content or passages if available. This helps the AI better understand the context."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-bold uppercase tracking-wider text-xs shadow-md"
                    >
                      Generate {selectedAction?.text}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="p-4 bg-gradient-to-r from-red-50 to-red-100 border-t border-red-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-sm text-red-700 font-medium">{error}</span>
              </div>
              <button
                onClick={onClearError}
                className="w-6 h-6 bg-red-200 hover:bg-red-300 rounded-full flex items-center justify-center transition-colors"
              >
                <XMarkIcon className="w-4 h-4 text-red-600" />
              </button>
            </div>
          </div>
        )}

        {/* Action Chips - Scrollable Suggestions */}
        <div className="px-4 py-2 border-t border-slate-50 bg-white overflow-hidden">
          <div className="flex overflow-x-auto scrollbar-hide space-x-2 py-1">
            {[
              { text: "Study Plan", icon: AcademicCapIcon, prompt: "Create a personalized study plan" },
              { text: "Explain Tip", icon: LightBulbIcon, prompt: "Explain this concept in simple terms" },
              { text: "Practice Qs", icon: BookOpenIcon, prompt: "Give me practice questions" },
              { text: "Exam Advice", icon: QuestionMarkCircleIcon, prompt: "Give me exam tips" },
              { text: "FAQ", icon: InformationCircleIcon, prompt: "Tell me about Profs Solutions" }
            ].map((chip, i) => (
              <button
                key={i}
                onClick={() => {
                  setMessage(chip.prompt);
                  handleSendMessage();
                }}
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-slate-50 hover:bg-indigo-50 text-slate-600 hover:text-indigo-600 border border-slate-200 hover:border-indigo-200 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap"
              >
                <chip.icon className="w-3.5 h-3.5" />
                <span>{chip.text}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Input - Standard Chatbot Style */}
        <div className="p-4 bg-white">
          <div className="relative bg-slate-50 border border-slate-200 rounded-2xl transition-all duration-200 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 focus-within:bg-white overflow-hidden">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder="Type your message..."
              rows={1}
              className="w-full pl-4 pr-12 py-3.5 bg-transparent border-none focus:ring-0 text-gray-800 placeholder-gray-400 text-sm resize-none max-h-32 overflow-y-auto"
            />
            <div className="absolute right-2 bottom-2">
              <button
                onClick={handleSendMessage}
                disabled={!message.trim() || isTyping}
                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 ${message.trim() && !isTyping
                  ? 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-700'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
              >
                <PaperAirplaneIcon className={`w-4 h-4 ${message.trim() ? '-rotate-45 relative left-0.5 bottom-0.5' : ''}`} />
              </button>
            </div>
          </div>
          <p className="mt-2 text-[10px] text-slate-400 text-center font-medium">
            Profstudymate • Version 2.0
          </p>
        </div>

        {/* Custom Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
              {/* Header */}
              <div className={`px-6 py-4 rounded-t-2xl ${confirmModalData.type === 'success' ? 'bg-emerald-50' :
                confirmModalData.type === 'error' ? 'bg-rose-50' : 'bg-indigo-50'
                }`}>
                <div className="flex items-center">
                  {confirmModalData.type === 'success' ? (
                    <CheckCircleIcon className="w-6 h-6 text-emerald-600 mr-3" />
                  ) : confirmModalData.type === 'error' ? (
                    <ExclamationTriangleIcon className="w-6 h-6 text-rose-600 mr-3" />
                  ) : (
                    <InformationCircleIcon className="w-6 h-6 text-indigo-600 mr-3" />
                  )}
                  <h3 className={`text-lg font-bold tracking-tight ${confirmModalData.type === 'success' ? 'text-emerald-800' :
                    confirmModalData.type === 'error' ? 'text-rose-800' : 'text-indigo-800'
                    }`}>
                    {confirmModalData.title}
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                <p className="text-gray-700 text-sm leading-relaxed">
                  {confirmModalData.message}
                </p>
              </div>

              {/* Actions */}
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
                <button
                  onClick={confirmModalData.onConfirm}
                  className={`px-8 py-2.5 rounded-xl font-bold uppercase tracking-widest text-xs transition-all shadow-md hover:shadow-lg ${confirmModalData.type === 'success'
                    ? 'bg-green-600 text-white hover:bg-green-700' :
                    confirmModalData.type === 'error'
                      ? 'bg-red-600 text-white hover:bg-red-700' :
                      'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AIChatbot;
